import { createApiClient } from "#/shared/api/client";

/**
 * How long a preview may run before the request is abandoned. The server calls the EHR live while the
 * request waits, so a preview gets longer than the API client's `REQUEST_TIMEOUT_MS`.
 */
export const PREVIEW_TIMEOUT_MS = 60_000;

/**
 * Why a preview failed. The server answers with the first four; the rest are decided here.
 */
export const PreviewErrorCode = Object.freeze({
  FORBIDDEN: "forbidden",
  NOT_FOUND: "not_found",
  UNSUPPORTED_EHR: "unsupported_ehr",
  EHR_REQUEST_FAILED: "ehr_request_failed",
  UNAUTHORIZED: "unauthorized",
  TIMEOUT: "timeout",
  INVALID_RESPONSE: "invalid_response",
  REQUEST_FAILED: "request_failed",
});

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

function previewError(code, message, warning = null) {
  return Object.assign(new Error(message), { code, warning });
}

function isPayload(data) {
  return data !== null && typeof data === "object" && Array.isArray(data.sections);
}

function isTimeout(error) {
  return error?.cause?.name === "TimeoutError";
}

// Returns a failed request as an error carrying a `PreviewErrorCode`. A JSON error body names its own code.
// Anything else (an HTML page from an access redirect, a proxy error) is classified as a timeout or by its status.
function toPreviewError(error) {
  const data = error?.data;

  if (typeof data?.error === "string") {
    return previewError(data.error, data.message ?? error.message, data.notInEhrWarning ?? null);
  }

  if (isTimeout(error)) {
    return previewError(PreviewErrorCode.TIMEOUT, error.message);
  }

  if (error?.status === HTTP_UNAUTHORIZED) {
    return previewError(PreviewErrorCode.UNAUTHORIZED, data?.errors ?? error.message);
  }

  if (error?.status === HTTP_FORBIDDEN) {
    return previewError(PreviewErrorCode.FORBIDDEN, error.message);
  }

  return previewError(PreviewErrorCode.REQUEST_FAILED, error?.message ?? "");
}

/**
 * Creates the Slot Inspector's server API.
 *
 * @param {String} previewUrl - The Slot Inspector's JSON endpoint.
 */
export const createSlotInspectorApi = (previewUrl) => {
  const client = createApiClient({ parse: false, timeout: PREVIEW_TIMEOUT_MS });

  /**
   * Requests a preview of the next slot import for a record.
   *
   * @param {Object} request
   * @param {String} request.recordType - `physician` or `location`.
   * @param {String} request.recordId
   * @param {String} request.startDate - First day of the preview, as `Y-m-d`.
   * @returns {Promise<Object>} The preview payload.
   * @throws {Error} With `code` (a `PreviewErrorCode`) and `warning` (the not-in-EHR warning, or null).
   */
  async function preview({ recordType, recordId, startDate }) {
    let data;

    try {
      data = await client.post(previewUrl, { [`${recordType}_id`]: recordId, start_date: startDate });
    } catch (error) {
      throw toPreviewError(error);
    }

    // A redirect can end on an HTML page that still answers 200.
    if (!isPayload(data)) {
      throw previewError(PreviewErrorCode.INVALID_RESPONSE, "The preview response was not a Slot Inspector payload.");
    }

    return data;
  }

  return { preview };
};
